import { useMemo, useState } from "react";
import { useNavigate, useParams, useSearch } from "@tanstack/react-router";
import { PMBadge, PMDataTable, PMEmptyState, PMFilterBar, PMSegmented, PMSkeleton, PMTabs } from "@/ds/vendor";
import { useApp } from "@/app/AppContext";
import { useLocations } from "@/data/queries";
import { levelDef } from "@/lib/levels";
import { Page } from "@/ui/Page";
import { LocationMap } from "@/ui/LocationMap";
import { relTime } from "@/ui/format";
import "./locations.css";

// One page for every level of the team's hierarchy (Farms / Fields, Clients /
// Farms, USDA, dealer). v1 had three separate ways in; this is the one.
export function recency(last?: string | null): { label: string; badge: "success" | "warning" | "error" | "neutral"; tone: "primary" | "warning" | "audio" } {
  if (!last) return { label: "Never visited", badge: "neutral", tone: "audio" };
  const days = (Date.now() - new Date(last).getTime()) / 86_400_000;
  if (days <= 7) return { label: relTime(last), badge: "success", tone: "primary" };
  if (days <= 21) return { label: relTime(last), badge: "warning", tone: "warning" };
  return { label: relTime(last), badge: "error", tone: "audio" };
}

export function LocationsPage() {
  const { teamId } = useParams({ from: "/t/$teamId/locations" });
  const search = useSearch({ from: "/t/$teamId/locations" });
  const nav = useNavigate();
  const { hierarchy } = useApp();
  const level = search.level || hierarchy.levels[0].key;
  const def = levelDef(level, hierarchy.key);
  const all = useLocations();
  const view = search.view || "split";
  const [hover, setHover] = useState<string | null>(null);
  const set = (patch: Record<string, string | undefined>) => nav({ to: ".", search: { ...search, ...patch }, replace: true } as any);

  const rows = useMemo(() => {
    const q = (search.q || "").toLowerCase();
    return all.data.filter((l) => (l.level || hierarchy.levels.at(-1)!.key) === level && (!q || `${l.label} ${l.parent_label} ${l.description}`.toLowerCase().includes(q)) && (!search.stale || recency(l.last_visit).tone === "audio"));
  }, [all.data, level, search.q, search.stale, hierarchy]);
  const counts = Object.fromEntries(hierarchy.levels.map((l) => [l.key, all.data.filter((x) => (x.level || hierarchy.levels.at(-1)!.key) === l.key).length]));
  const overdue = rows.filter((r) => recency(r.last_visit).tone === "audio").length;
  const open = (id: string) => nav({ to: "/t/$teamId/locations/$locationId", params: { teamId, locationId: id } });
  const parentLevel = hierarchy.levels[hierarchy.levels.findIndex((l) => l.key === level) - 1];

  const table = (
    <PMDataTable
      rowKey="id"
      rows={rows}
      onRowClick={(r: any) => open(r.id)}
      maxHeight={view === "split" ? 620 : undefined}
      emptyText={`No ${def.plural.toLowerCase()} match`}
      columns={[
        { key: "label", label: def.label, sortable: true, render: (r: any) => <span className="loc-name" onMouseEnter={() => setHover(r.id)} onMouseLeave={() => setHover(null)}>{def.emoji} <strong>{r.label}</strong></span> },
        ...(parentLevel ? [{ key: "parent_label", label: parentLevel.label, sortable: true, render: (r: any) => r.parent_label || <span className="subtle">—</span> }] : []),
        ...(view === "table" ? [{ key: "description", label: "Notes", render: (r: any) => <span className="muted clamp-2">{r.description}</span> }] : []),
        { key: "visit_count", label: "Visits", align: "right" as const, sortable: true },
        { key: "last_visit", label: "Last visit", sortable: true, render: (r: any) => { const rc = recency(r.last_visit); return <PMBadge variant={rc.badge} dot>{rc.label}</PMBadge>; } },
      ]}
    />
  );

  return (
    <Page
      chip={hierarchy.label.toUpperCase()}
      title={def.plural}
      subtitle={`${rows.length} ${rows.length === 1 ? def.label.toLowerCase() : def.plural.toLowerCase()} · ${overdue} not visited in 3 weeks`}
      actions={<PMSegmented value={view} onChange={(v: string) => set({ view: v === "split" ? undefined : v })} options={[{ value: "split", label: "Map" }, { value: "table", label: "Table" }]} />}
      wide
    >
      {hierarchy.levels.length > 1 && <PMTabs value={level} onChange={(id: string) => set({ level: id === hierarchy.levels[0].key ? undefined : id })} tabs={hierarchy.levels.map((l) => ({ id: l.key, label: `${l.emoji} ${l.plural}`, count: counts[l.key] }))} aria-label="Level" />}
      <PMFilterBar search={search.q || ""} searchPlaceholder={`Search ${def.plural.toLowerCase()}`} onSearch={(q: string) => set({ q: q || undefined })}
        activeChips={search.stale ? [{ key: "stale", label: "Not visited in 3 weeks", dotColor: "var(--pm-data-audio)" }] : []}
        onRemoveChip={() => set({ stale: undefined })} onClearAll={() => set({ stale: undefined, q: undefined })} />
      {all.isLoading && !all.data.length ? <PMSkeleton shape="rect" height={420} radius={16} /> : !rows.length && !search.q && !search.stale ? (
        <PMEmptyState tone="brand" title={`No ${def.plural.toLowerCase()} yet`} description={`${def.plural} are created on the phone app or imported in classic.`} />
      ) : view === "table" ? (
        <div className="card">{table}</div>
      ) : (
        <div className="loc-split">
          <div className="card loc-table">{table}</div>
          <div className="loc-map">
            <LocationMap
              height="100%"
              selectedId={hover}
              onSelect={open}
              items={rows.map((r) => { const rc = recency(r.last_visit); return { id: r.id, label: r.label || "", geometry: r.geometry, latitude: r.latitude, longitude: r.longitude, tone: rc.tone, sub: rc.label === "Never visited" ? rc.label : `Visited ${rc.label}` }; })}
            />
            <div className="map-legend pm-glass"><span><i style={{ background: "var(--pm-primary-light)" }} />≤ 1 week</span><span><i style={{ background: "var(--pm-warning)" }} />1–3 weeks</span><span><i style={{ background: "var(--pm-data-audio)" }} />Overdue</span></div>
          </div>
        </div>
      )}
    </Page>
  );
}
